import { useState, type FormEvent, type ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { ROL_LABELS } from '../../lib/roles';
import type { RolCodigo, RolRow } from '../../types/database.types';

interface UsuarioConRol {
  id: string;
  nombre: string;
  email: string;
  telefono: string | null;
  estado: 'activo' | 'inactivo' | 'anulado';
  rol_id: string;
  rol_codigo: RolCodigo;
}

/** Roles internos que se pueden crear desde este formulario — "clienta"
 * no aplica aquí, esa se maneja desde el módulo de Clientas / el portal. */
const ROLES_INTERNOS: RolCodigo[] = ['admin', 'recepcion', 'profesional'];

/**
 * Módulo de Usuarios (DAF v2, sección 4 — "Usuarios" y "Roles y
 * permisos"). Crear un usuario interno requiere dar de alta su login en
 * Supabase Auth, algo que el frontend no puede hacer con la clave
 * pública — por eso pasa por la Edge Function `crear-usuario-interno`
 * (supabase/functions/crear-usuario-interno/), la única pieza de este
 * proyecto que corre con la service role key, y solo para esto. Esa
 * función vuelve a verificar server-side que quien llama sea admin
 * antes de crear nada — el botón de aquí es solo UX, no la barrera de
 * seguridad real.
 *
 * Nota sobre "Roles y permisos": los roles (`roles`) sí gobiernan el
 * acceso real, vía las funciones `is_admin()`/`is_staff()` que usan las
 * políticas RLS de schema.sql. El catálogo fino `permisos`/`rol_permisos`
 * existe en la base de datos para permisos granulares futuros, pero
 * ninguna política de este scaffold lo consulta todavía — por eso esta
 * pantalla no expone un editor de esa matriz: sería mostrar un control
 * que hoy no cambia nada real.
 */
export function UsuariosList() {
  const { perfil } = useAuth();
  const [mostrarForm, setMostrarForm] = useState(false);

  const { data: roles } = useSupabaseQuery<RolRow[]>(async () => supabase.from('roles').select('*').order('nombre'));

  const {
    data: usuarios,
    loading,
    error,
    refetch,
  } = useSupabaseQuery<UsuarioConRol[]>(async () => {
    const { data, error } = await supabase.from('usuarios').select('*, roles ( codigo )').order('nombre');

    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => ({
      id: row.id as string,
      nombre: row.nombre as string,
      email: row.email as string,
      telefono: row.telefono as string | null,
      estado: row.estado as UsuarioConRol['estado'],
      rol_id: row.rol_id as string,
      rol_codigo: (row.roles as { codigo: RolCodigo } | null)?.codigo ?? 'clienta',
    }));

    return { data: items, error: null };
  });

  async function cambiarRol(usuarioId: string, rolId: string) {
    await supabase.from('usuarios').update({ rol_id: rolId }).eq('id', usuarioId);
    refetch();
  }

  async function alternarEstado(usuario: UsuarioConRol) {
    const nuevoEstado = usuario.estado === 'activo' ? 'inactivo' : 'activo';
    await supabase.from('usuarios').update({ estado: nuevoEstado }).eq('id', usuario.id);
    refetch();
  }

  function alCrear() {
    setMostrarForm(false);
    refetch();
  }

  return (
    <div>
      <PageHeader
        title="Usuarios"
        description="Cuentas de acceso al panel — administradores, recepción y profesionales"
        actions={
          <button className="btn-primary" onClick={() => setMostrarForm((v) => !v)}>
            {mostrarForm ? 'Cancelar' : 'Crear usuario'}
          </button>
        }
      />

      {mostrarForm && <CrearUsuarioForm roles={(roles ?? []).filter((r) => ROLES_INTERNOS.includes(r.codigo))} onCreado={alCrear} onCancelar={() => setMostrarForm(false)} />}

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {usuarios && usuarios.length === 0 && <EmptyState title="No hay usuarios registrados todavía" />}

      {usuarios && usuarios.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Nombre</th>
                <th className="px-4 py-3 font-medium">Correo</th>
                <th className="px-4 py-3 font-medium">Teléfono</th>
                <th className="px-4 py-3 font-medium">Rol</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => (
                <tr key={u.id} className="border-b border-onyx/5 last:border-0">
                  <td className="px-4 py-3 text-onyx">{u.nombre}</td>
                  <td className="px-4 py-3 text-onyx/70">{u.email}</td>
                  <td className="px-4 py-3 text-onyx/70">{u.telefono ?? '—'}</td>
                  <td className="px-4 py-3">
                    <select
                      value={u.rol_id}
                      onChange={(e) => cambiarRol(u.id, e.target.value)}
                      disabled={u.id === perfil?.id}
                      title={u.id === perfil?.id ? 'No puedes cambiar tu propio rol desde aquí' : undefined}
                      className="rounded-md border border-onyx/15 px-2 py-1 text-xs disabled:opacity-50"
                    >
                      {(roles ?? []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {ROL_LABELS[r.codigo] ?? r.nombre}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${u.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-onyx/10 text-onyx/50'}`}>
                      {u.estado === 'activo' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.id !== perfil?.id && (
                      <button className="text-xs text-onyx/60 hover:underline" onClick={() => alternarEstado(u)}>
                        {u.estado === 'activo' ? 'Desactivar' : 'Reactivar'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CrearUsuarioForm({
  roles,
  onCreado,
  onCancelar,
}: {
  roles: RolRow[];
  onCreado: () => void;
  onCancelar: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [password, setPassword] = useState('');
  const [rolCodigo, setRolCodigo] = useState<RolCodigo>('recepcion');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setError(null);

    const { data: sesion } = await supabase.auth.getSession();
    const token = sesion.session?.access_token;
    if (!token) {
      setError('Tu sesión expiró — vuelve a iniciar sesión e intenta de nuevo.');
      setGuardando(false);
      return;
    }

    const { data, error: invokeError } = await supabase.functions.invoke('crear-usuario-interno', {
      body: { nombre, email, telefono: telefono || null, password, rol_codigo: rolCodigo },
      headers: { Authorization: `Bearer ${token}` },
    });

    setGuardando(false);

    const mensajeError = invokeError ? invokeError.message : (data as { error?: string } | null)?.error;
    if (mensajeError) {
      setError(mensajeError);
      return;
    }

    onCreado();
  }

  return (
    <form onSubmit={enviar} className="card mb-6 space-y-4">
      <p className="text-sm text-onyx/60">
        Se crea el login y la cuenta interna en un solo paso. La persona podrá iniciar sesión de inmediato con el correo y la
        contraseña que definas aquí — pídele que la cambie desde su primer ingreso.
      </p>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Campo label="Nombre completo">
          <input required value={nombre} onChange={(e) => setNombre(e.target.value)} className="input-field" />
        </Campo>
        <Campo label="Rol">
          <select value={rolCodigo} onChange={(e) => setRolCodigo(e.target.value as RolCodigo)} className="input-field">
            {roles.map((r) => (
              <option key={r.id} value={r.codigo}>
                {ROL_LABELS[r.codigo] ?? r.nombre}
              </option>
            ))}
          </select>
        </Campo>
        <Campo label="Correo electrónico">
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-field" />
        </Campo>
        <Campo label="Teléfono (opcional)">
          <input value={telefono} onChange={(e) => setTelefono(e.target.value)} className="input-field" />
        </Campo>
        <Campo label="Contraseña temporal">
          <input
            required
            type="text"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-field"
            placeholder="Al menos 8 caracteres"
          />
        </Campo>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancelar} className="btn-secondary">
          Cancelar
        </button>
        <button type="submit" disabled={guardando} className="btn-primary disabled:opacity-50">
          {guardando ? 'Creando…' : 'Crear usuario'}
        </button>
      </div>
    </form>
  );
}

function Campo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-onyx/80">{label}</span>
      {children}
    </label>
  );
}
